// ═══════════════════════════════════════════════════════════════
// Settings — Reusable Permission Matrix Component
// Shows per-module permission levels with clickable badges
// ═══════════════════════════════════════════════════════════════

import { Badge, Group, Text, Stack } from '../../components/gab/GabComponents';
import { MODULES } from '../../lib/modules';
import { ALL_MODULE_IDS } from '../../types/rbac';
import type { RbacPermissions, PermissionLevel, ModuleId } from '../../types/rbac';

interface PermissionMatrixProps {
  permissions: RbacPermissions;
  onChange: (permissions: RbacPermissions) => void;
  readOnly?: boolean;
}

const LEVEL_ORDER: PermissionLevel[] = ['none', 'viewer', 'editor', 'admin'];

const LEVEL_COLORS: Record<PermissionLevel, string> = {
  none: 'gray',
  viewer: 'blue',
  editor: 'yellow',
  admin: 'green',
};

const LEVEL_LABELS: Record<PermissionLevel, string> = {
  none: 'None',
  viewer: 'Viewer',
  editor: 'Editor',
  admin: 'Admin',
};

function getModuleName(moduleId: ModuleId): string {
  return MODULES.find(m => m.id === moduleId)?.name ?? moduleId;
}

function cycleLevel(current: PermissionLevel): PermissionLevel {
  const idx = LEVEL_ORDER.indexOf(current);
  return LEVEL_ORDER[(idx + 1) % LEVEL_ORDER.length];
}

export default function PermissionMatrix({ permissions, onChange, readOnly }: PermissionMatrixProps) {
  const handleClick = (moduleId: ModuleId) => {
    if (readOnly) return;
    const next = cycleLevel(permissions[moduleId] ?? 'none');
    onChange({ ...permissions, [moduleId]: next });
  };

  return (
    <Stack gap="xs">
      {ALL_MODULE_IDS.map(moduleId => {
        const level = permissions[moduleId] ?? 'none';
        return (
          <Group key={moduleId} justify="space-between" px="xs" py={4}
            style={{ borderRadius: 4, background: 'var(--mantine-color-gray-0)' }}
          >
            <Text size="sm" fw={500}>{getModuleName(moduleId)}</Text>
            <Badge
              color={LEVEL_COLORS[level]}
              variant="filled"
              size="md"
              style={{ cursor: readOnly ? 'default' : 'pointer', minWidth: 64, textAlign: 'center' }}
              onClick={() => handleClick(moduleId)}
            >
              {LEVEL_LABELS[level]}
            </Badge>
          </Group>
        );
      })}
    </Stack>
  );
}
